import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { type IIntegration } from '@principle-theorem/integrations';
import {
  AccountingFunctionsService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import { StripeFunctions, StripeService } from '@principle-theorem/ng-stripe';
import {
  Brand,
  getBaseTransaction,
  Invoice,
  Organisation,
  Practice,
  Transaction,
} from '@principle-theorem/principle-core';
import {
  type IInvoice,
  type IOrganisation,
  type ITransaction,
  TransactionAction,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type WithRef,
  asReffable,
  filterUndefined,
  isFailedPreconditionError,
  Region,
} from '@principle-theorem/shared';
import { type ICreatePaymentIntentResponse } from '@principle-theorem/stripe';
import {
  type IStripeIntegrationData,
  StripeIntegrationStorage,
} from '@principle-theorem/stripe-integration';
import {
  type ConfirmCardPaymentData,
  type PaymentIntent,
  type Stripe,
} from '@stripe/stripe-js';
import { type Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  type ITransactionProvider,
  TransactionProviderType,
} from '../transaction-provider';
import {
  type IStripeFormData,
  type IStripeTransactionInput,
  staffStripeAccountErrorMessage,
  StripeTransactionComponent,
} from './stripe-transaction.component';

export const STRIPE_ACCOUNT_CHANGED_MESSAGE =
  'Stripe account changed. Please try the payment again.';

@Injectable()
export class StripeTransactionProvider implements ITransactionProvider {
  providerId = TransactionProvider.Stripe;
  providerType = TransactionProviderType.Payment;
  providerRegions = [Region.Australia, Region.NewZealand];
  isEnabled$: Observable<boolean>;

  constructor(
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _functions: StripeFunctions,
    private _stripe: StripeService,
    organisationService: OrganisationService,
    private _accountFunctions: AccountingFunctionsService
  ) {
    const storage = new StripeIntegrationStorage();
    this.isEnabled$ = organisationService.integrationCol$.pipe(
      filterUndefined(),
      switchMap((integrationsCol) =>
        storage.get$(storage.col(integrationsCol))
      ),
      map((data) => !!data)
    );
  }

  canCapture$(invoice: WithRef<IInvoice>): Observable<boolean> {
    const canCapture =
      !Invoice.isPaid(invoice) && Invoice.canAddTransactions(invoice);
    return of(canCapture);
  }

  async capture(
    invoice: WithRef<IInvoice>
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const orgRef = this._resolveInvoiceOrg(invoice);
    const connectedAccount = await this._getStripeIntegration(orgRef);
    if (!connectedAccount) {
      this._snackbar.open(staffStripeAccountErrorMessage);
      return;
    }

    const { amount } = await getBaseTransaction(invoice);
    const data: IStripeTransactionInput = {
      amount: amount || 0,
      connectedAccountId: connectedAccount.data.connectedAccountId,
      invoice,
    };

    const formData = await this._dialog
      .open<
        StripeTransactionComponent,
        IStripeTransactionInput,
        IStripeFormData
      >(StripeTransactionComponent, DialogPresets.medium({ data }))
      .afterClosed()
      .toPromise();

    if (!formData) {
      this._snackbar.open('Transaction Cancelled');
      return;
    }

    let intent: ICreatePaymentIntentResponse;
    try {
      intent = await this._functions.createPaymentIntent(
        orgRef.id,
        formData.amount
      );
    } catch (error) {
      if (isFailedPreconditionError(error)) {
        this._snackbar.open(`Transaction Failed: ${error.message}`);
        return;
      }
      throw error;
    }
    if (
      intent.connectedAccountId !== connectedAccount.data.connectedAccountId
    ) {
      this._snackbar.open(STRIPE_ACCOUNT_CHANGED_MESSAGE);
      return;
    }

    const stripe = await this._stripe.forAccount(intent.connectedAccountId);
    if (!stripe) {
      this._stripe.showStripeInitError();
      return;
    }

    const pending = await this._addPendingTransaction(
      invoice,
      intent,
      formData
    );
    if (!pending) {
      return;
    }
    const success = await this._processPayment(
      stripe,
      intent,
      formData.token.id
    );
    const transactionRef = await this._resolveTransaction(
      invoice,
      pending,
      success ? TransactionStatus.Complete : TransactionStatus.Failed
    );
    return success ? transactionRef : undefined;
  }

  private async _processPayment(
    stripe: Stripe,
    intent: ICreatePaymentIntentResponse,
    token: string
  ): Promise<PaymentIntent | undefined> {
    const paymentData: ConfirmCardPaymentData = {
      payment_method: {
        card: { token },
      },
    };
    const { paymentIntent, error } = await stripe.confirmCardPayment(
      intent.clientSecret,
      paymentData
    );
    if (error || !paymentIntent) {
      const message = error?.message || `Couldn't process payment`;
      this._snackbar.open(`Transaction Failed: ${message}`);
      return;
    }
    return paymentIntent;
  }

  private async _addPendingTransaction(
    invoice: WithRef<IInvoice>,
    intent: ICreatePaymentIntentResponse,
    formData: IStripeFormData
  ): Promise<WithRef<ITransaction> | undefined> {
    const baseTransaction = await getBaseTransaction(invoice);
    const pending: ITransaction = Transaction.init({
      ...baseTransaction,
      reference: intent.reference,
      connectedAccountId: intent.connectedAccountId,
      type: TransactionType.Incoming,
      status: TransactionStatus.Pending,
      amount: formData.amount,
      provider: TransactionProvider.Stripe,
      practiceRef: formData.practiceRef,
    });
    const pendingRef = await this._accountFunctions.addTransactionToInvoice(
      invoice,
      pending,
      TransactionAction.Add
    );
    if (!pendingRef) {
      return;
    }
    return { ...pending, ref: pendingRef };
  }

  private async _resolveTransaction(
    invoice: WithRef<IInvoice>,
    pending: WithRef<ITransaction>,
    status: TransactionStatus
  ): Promise<DocumentReference<ITransaction> | undefined> {
    const resolved = Transaction.resolveAs(
      pending,
      status,
      pending.connectedAccountId
        ? { connectedAccountId: pending.connectedAccountId }
        : undefined
    );
    const received: WithRef<ITransaction> = { ...resolved, ref: pending.ref };
    return this._accountFunctions.addTransactionToInvoice(
      invoice,
      received,
      TransactionAction.Update
    );
  }

  private _resolveInvoiceOrg(
    invoice: WithRef<IInvoice>
  ): DocumentReference<IOrganisation> {
    const practiceRef = invoice.practice.ref;
    const brandRef = Practice.brandRef(asReffable(practiceRef));
    const orgRef = Brand.organisationRef(asReffable(brandRef));
    if (!orgRef) {
      throw new Error(`Couldn't find Organisation`);
    }
    return orgRef;
  }

  private _getStripeIntegration(
    orgRef: DocumentReference<IOrganisation>
  ): Promise<IIntegration<IStripeIntegrationData> | undefined> {
    const storage = new StripeIntegrationStorage();
    return storage.get(
      Organisation.integrationCol<IStripeIntegrationData>({
        ref: orgRef,
      })
    );
  }
}
